import api from "@/api";

const defaultGroup = {
  name: "",
};

export default {
  namespaced: true,

  state: {
    group: defaultGroup,
    collection: [],
    error: {},
    feed: [],
    userGroups: [],
  },

  mutations: {
    SET_COLLECTION(state, collection) {
      state.collection = collection;
    },

    SET_GROUP(state, payload) {
      state.group = payload;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    UPDATE_FEED(state, payload) {
      state.feed = [...state.feed, ...payload];
    },
    RESET_FEED(state) {
      state.feed = [];
    },
    SET_USER_GROUPS(state, payload) {
      state.userGroups = payload;
    },
  },

  actions: {
    async list({ commit }, params) {
      let resp;
      let endpoint = "/interestgroups";

      if (params && params.flat) {
        endpoint = `${endpoint}/flat`;
      }
      try {
        resp = await api().get(endpoint);
        commit("SET_COLLECTION", resp.data.data.interestgroup);
        return Promise.resolve(resp.data.data.interestgroup);
      } catch (err) {
        // if there are just no groups
        if (err.response.data.data.length < 1 && err.response.status === 404) {
          commit("SET_COLLECTION", []);
          return Promise.resolve([]);
        }
        return Promise.reject({ ...err });
      }
    },

    async join(context, id) {
      let response;

      try {
        response = await api().post(`/interestgroup/${id}/add`);
        return Promise.resolve(response);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    async leave(context, id) {
      let response;

      try {
        response = await api().post(`/interestgroup/${id}/remove`);
        return Promise.resolve(response);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    async read({ commit }, id) {
      let resp;
      try {
        resp = await api().get(`/interestgroup/${id}`);
        commit("SET_GROUP", resp.data.data.interestgroup[0]);
        return Promise.resolve(resp.data.data.interestgroup[0]);
      } catch (err) {
        commit("SET_ERROR", { ...err });
        return Promise.reject({ ...err });
      }
    },

    async create({ commit, rootState }, groupData) {
      let resp;
      try {
        resp = await api().post("/interestgroup", { interestgroup: groupData });
        rootState.dispatch(
          "notifications/create",
          {
            body: "The interest group has been created",
            type: "success",
            timeout: 12000,
          },
          { root: true }
        );
        return Promise.resolve(resp.data.data);
      } catch (err) {
        commit("SET_ERROR", { ...err });
        return Promise.reject({ ...err });
      } finally {
        return "not sure";
      }
    },

    async update({ commit, rootState }, { id, groupData }) {
      let resp;
      try {
        resp = await api().post(`/interestgroup/${id}`, {
          interestgroup: groupData,
        });
        rootState.dispatch(
          "notifications/create",
          {
            body: "The interest group has been updated",
            type: "success",
            timeout: 12000,
          },
          { root: true }
        );
        return Promise.resolve(resp.data.data);
      } catch (err) {
        commit("SET_ERROR", { ...err });
        return Promise.reject({ ...err });
      } finally {
        return "not sure";
      }
    },

    async delete({ commit, rootState }, id) {
      let resp;
      try {
        resp = await api().delete(`/interestgroup/${id}`);
        rootState.dispatch(
          "notifications/create",
          {
            body: "The interest group has been deleted",
            type: "success",
            timeout: 12000,
          },
          { root: true }
        );
        commit("SET_GROUP", defaultGroup);
        return Promise.resolve(resp.data.data);
      } catch (err) {
        commit("SET_ERROR", { ...err });
        return Promise.reject({ ...err });
      } finally {
        return "not sure";
      }
    },

    async getUserInterestGroups({ commit, rootState }, forceRefresh = false) {
      let groupInfo;
      let groupIds;
      const { userId } = rootState.auth;

      const userGroups = localStorage.getItem(
        "mft_groups_getUserInterestGroups"
      );

      if (
        !userGroups ||
        JSON.parse(userGroups).timestamp < new Date().getTime() - 6000 * 150 ||
        forceRefresh
      ) {
        try {
          groupInfo = await api().get(`/user/${userId}/interestgroups`);
          groupIds = groupInfo.data.data.interestgroup.map((grp) => grp.id);
          localStorage.setItem(
            "mft_groups_getUserInterestGroups",
            JSON.stringify({
              groupIds: groupIds || [],
              timestamp: new Date().getTime(),
            })
          ); // we only need ids
        } catch (err) {
          const errObj = { ...err };
          if (errObj.response.data.data.length === 0) {
            groupIds = [];
            localStorage.setItem(
              "mft_groups_getUserInterestGroups",
              JSON.stringify({
                groupIds: [],
                timestamp: new Date().getTime(),
              })
            ); // we only need ids
          } else {
          }
        }
      } else {
        const parsed = JSON.parse(userGroups);
        if (parsed.groupIds) {
          groupIds = parsed.groupIds;
        } else {
          groupIds = [];
        }
      }

      commit("SET_USER_GROUPS", groupIds);
      return Promise.resolve(groupIds);
    },

    /**
     * For this we generate a feed or items related to interest groups
     * This is news, events, content, collaboration
     * DEPRECATED: Now not used as it is unecessary
     */
    async feed({ commit, dispatch, rootState }) {
      // get anu interest groups
      let groupinfo;
      let news;
      let events;
      let content;
      const userid = rootState.auth.userId;

      const groupids = await dispatch(
        "InterestGroups/getUserInterestGroups",
        null,
        { root: true }
      );

      // get the news first
      const filter = {
        sort: [
          {
            created_time: {
              order: "desc",
            },
          },
        ],
        query: {
          bool: {
            must: [
              {
                terms: {
                  interestgroups: JSON.parse(groupids),
                },
              },
              {
                term: {
                  status: "approved",
                },
              },
            ],
          },
        },
        size: 5,
      };

      try {
        // remove news filter just in case
        console.log("newsfilter", filter);
        commit("News/SET_TRUST_FILTER", false, { root: true });
        news = await api().put("/news/articles/web", { filter });
        commit("UPDATE_FEED", news.data.data.article);
      } catch (err) {
        // return no items
        commit("UPDATE_FEED", []);
        console.log({ ...err });
      }

      // same for events
      try {
        events = await api().put("/schedule/events", { filter });
        // events = await api().get('/schedule/events');
        commit("UPDATE_FEED", events.data.data.event);
      } catch (err) {
        commit("UPDATE_FEED", []);
        console.log(err);
      }

      // content as well
      try {
        content = await api().put("/content/pages", { filter });
        // content = await api().get('/content/pages');
        commit("UPDATE_FEED", content.data.data.page);
      } catch (err) {
        commit("UPDATE_FEED", []);
        console.log(err);
      }
      return Promise.resolve();
    },

    setCurrentGroup({ commit }, id) {
      commit("SET_GROUP", id);
    },

    async getUserGroups({ state, commit, rootState }) {
      let groupinfo;
      const { userid } = rootState.auth;
      try {
        groupinfo = await api().get("/currentuser/interestgroups");
        commit("SET_USER_GROUPS", groupinfo.data.data.interestgroups);
      } catch (err) {
        commit("SET_USER_GROUPS", []);
      }
    },
  },
  getters: {
    interestFeed(state) {
      // return ['test','test2']
      return state.feed;
    },
    localGroups() {
      return JSON.parse(
        localStorage.getItem("mft_groups_getUserInterestGroups").groupIds
      );
    },
  },
};
